import { LgPhrase } from "./LgPhrase";
import { useRef, type ReactNode } from "react";
import { Modal } from "@noiman/ui/modal";
import { Button } from "@noiman/ui/button";
import { HandIcon } from "./HandIcon";
import { useFocusScope, useModalOpenMark } from "./focus-scope";
import { LgRoot, useLgTheme } from "./LgRoot";

export type LgModalProps = {
  open: boolean;
  onClose: () => void;
  /** 題。ダイアログの名前にもなる */
  title: string;
  children: ReactNode;
  /** 下の操作の並び (LgButton と LgPrimaryAction)。無くてもよい */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** 外を押しても閉じない (入力の途中を守るとき) */
  closeOnOverlayClick?: boolean;
  closeLabel?: string;
  /** 中身を中央にそろえる窓 (祝いの窓) は、題も中央にそろえる */
  centered?: boolean;
};

/**
 * @noiman/ui の Modal を包んだダイアログ。Modal は body へ出るので .lg の外になる。
 * そこで中身を、今のテーマと明暗を引き継いだ LgRoot で包み、トークンを自分の中に作る。
 * 面、縁、角、題、閉じるボタンは luagate-ui が決める (Modal の面と影は bare で外す)。
 * 1 つの LgRoot なので、中の LgPrimaryAction は画面の側の 1 つと数えない
 */
function ModalInner({ onClose, title, children, footer, closeLabel, centered }: Pick<LgModalProps, "onClose" | "title" | "children" | "footer" | "centered"> & { closeLabel: string }) {
  const { theme, mode } = useLgTheme();
  const ref = useRef<HTMLElement>(null);
  // 開いたとき中へフォーカスを移して Tab を中で巡らせ、閉じたら開く前の要素へ戻す (Modal 自身はやらない)
  useFocusScope(ref, { trap: true });
  return (
      <LgRoot theme={theme} mode={mode} className={centered ? "lg-modal lg-modal--center" : "lg-modal"} innerRef={ref}>
        <div className="lg-modal__head">
          <h2 className="lg-h lg-h--section lg-modal__title"><LgPhrase>{title}</LgPhrase></h2>
          <Button variant="ghost" size="sm" type="button" className="lg-modal__close" onClick={onClose} aria-label={closeLabel}>
            <HandIcon name="sym-close" size={24} />
          </Button>
        </div>
        <div className="lg-modal__body">{children}</div>
        {footer ? <div className="lg-modal__foot">{footer}</div> : null}
      </LgRoot>
  );
}

export function LgModal({ open, onClose, title, children, footer, size = "md", closeOnOverlayClick = true, closeLabel = "閉じる", centered = false }: LgModalProps) {
  useModalOpenMark(open);
  return (
    <Modal isOpen={open} onClose={onClose} bare showCloseButton={false} bodyClassName="" flatOverlay size={size} ariaLabel={title} closeOnOverlayClick={closeOnOverlayClick}>
      <ModalInner onClose={onClose} title={title} footer={footer} closeLabel={closeLabel} centered={centered}>
        {children}
      </ModalInner>
    </Modal>
  );
}
